import { Image, Text, View } from "@react-pdf/renderer";
import { Trans, useTranslation } from "react-i18next";
import { COMPRESSION_PILE, TENSION_PILE } from "../../../constants";
import Inch from "../../Math/Units/Imperial/Inch";
import Kilopound from "../../Math/Units/Imperial/Kilopound";
import KilopoundPerSquareInch from "../../Math/Units/Imperial/KilopoundPerSquareInch";
import KiloNewton from "../../Math/Units/Metric/KiloNewton";
import Megapascal from "../../Math/Units/Metric/Megapascal";
import Millimetre from "../../Math/Units/Metric/Millimetre";
import { DataRow, DataRowTextValue } from "../DataRow";
import { PageWrapper, styles, tableStyles } from "../SmartTitanProjektPDF";
import { ABC_URI, imageURI } from "../Aashto/AASHTO_Innere";

export function FHWA_Innere({ data }) {
  const { t } = useTranslation(["translation", "fhwa"]);
  let itemIndex = 1;
  let isMetric = data.Fhwa.fhwaAll.measurementType == 0;
  let isCompression = data.Fhwa.fhwaAll.loadType === COMPRESSION_PILE;
  let isTension = data.Fhwa.fhwaAll.loadType === TENSION_PILE;
  const designLoad = data.Fhwa.fhwaAll.totalDesignLoad.current;
  const ultimateLoad = data.Fhwa.fhwaAll.ultimateLoad.current;
  const allowableLoad = data.Fhwa.fhwaAll.allowableLoad.current;

  var aL_isGreater = allowableLoad > designLoad;

  const allowableTestLoad = data.Fhwa.fhwaAll.allowableTestLoad.current;
  var aTL_isGreater = allowableTestLoad > ultimateLoad;

  var ok = t("AllowableLoadDesignCheck_Ok", { ns: "fhwa" });
  var ng = t("AllowableLoadDesignCheck_Error", { ns: "fhwa" });

  return (
    <>
      <PageWrapper Deckblatt={data.Deckblatt}>
        <View>
          {/* Innere Tragfähigkeit */}
          <Text style={styles.section}>{t("inner.headline")}</Text>
          <View style={tableStyles.tableContainer}>
            {/* Imperial / Metric: */}
            <DataRowTextValue
              item={{
                title: t("aashto.Imperial / Metric"),
                value: t(isMetric ? "aashto.Metric" : "aashto.Imperial"),
                itemIndex: itemIndex++,
              }}
            />
            {/* Belastungsart/-Richtung */}
            <DataRowTextValue
              item={{
                title: t("inner.loadType"),
                value: t(isCompression ? "inner.compressionPile" : "inner.tensionPile"),
                itemIndex: itemIndex++,
              }}
            />
          </View>
          {/* Information Druck */}
          {isCompression ? (
            <>
              <Text style={styles.sectionSubText}>{t("aashto.inner.Compression")}</Text>

              <View style={{ marginRight: 60 }}>
                {/*
                    f'c
                    Specified compressive strength of micropile grout at 28 days unless another and is specified (ksi)
                    Ag
                    Cross-Sectional Area of grout withing micropile (in2)
                    Fy
                    Specified minimum yield strength of reinforcement bar or stress in steel reinforcement bar at a strain of 0.003, whichever is less (ksi)
                    Ab
                    Cross-sectional area of steel reinfocing bar (in2)
                */}
                <View style={{ flexDirection: "row" }}>
                  <Image
                    src={imageURI(
                      "P_{c-allowable}=(\\alpha\\cdot&space;f^{'c}\\cdot&space;A_{grout}&plus;\\gamma\\cdot&space;F_{y-bar}\\cdot&space;A_{bar})"
                    )}
                    style={{
                      height: 11,
                      width: 205,
                      marginLeft: 10,
                    }}
                  />
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("\\alpha")}
                      style={{
                        height: 4,
                        width: 5,
                        marginLeft: 10,
                        marginTop: 8,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    {t("SafetyFactorGroutDesign", { ns: "fhwa" })}
                  </Text>
                </View>
                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("f'_c")}
                      style={{
                        height: 8,
                        width: 7,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    <Trans
                      i18nKey="aashto.inner.fc_Description"
                      values={{
                        unit: isMetric ? "MPa" : "ksi",
                      }}
                    />
                  </Text>
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("A_g")}
                      style={{
                        height: 9,
                        width: 10,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    <Trans
                      i18nKey="aashto.inner.Ag_Description"
                      values={{
                        unit: isMetric ? "mm²" : "in²",
                      }}
                    />
                  </Text>
                </View>
                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("\\gamma")}
                      style={{
                        height: 6,
                        width: 4,
                        marginLeft: 10,
                        marginTop: 8,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    {t("SafetyFactorSteel", { ns: "fhwa" })}
                  </Text>
                </View>
                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("F_y")}
                      style={{
                        height: 9,
                        width: 9,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text
                    style={{
                      marginTop: 5,
                      marginLeft: 10,
                      marginRight: 10,
                      fontSize: 8,
                    }}
                  >
                    <Trans
                      i18nKey="aashto.inner.Fy_Description"
                      values={{
                        unit: isMetric ? "MPa" : "ksi",
                      }}
                    />
                  </Text>
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("A_b")}
                      style={{
                        height: 7,
                        width: 9,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    <Trans
                      i18nKey="aashto.inner.Ab_Description"
                      values={{
                        unit: isMetric ? "mm²" : "in²",
                      }}
                    />
                  </Text>
                </View>
              </View>
            </>
          ) : null}
          {/* Information Zug */}
          {isTension ? (
            <>
              <Text style={styles.sectionSubText}>{t("aashto.inner.Tension")}</Text>

              <View style={{ marginRight: 60 }}>
                <View style={{ flexDirection: "row" }}>
                  <Image
                    src={imageURI("P_{t-allowable}=\\gamma\\cdot F_{y-bar}\\cdot A_{bar}")}
                    style={{
                      height: 9,
                      width: 125,
                      marginLeft: 10,
                    }}
                  />
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("F_y")}
                      style={{
                        height: 9,
                        width: 9,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text
                    style={{
                      marginTop: 5,
                      marginLeft: 10,
                      marginRight: 10,
                      fontSize: 8,
                    }}
                  >
                    <Trans
                      i18nKey="aashto.inner.Fy_Description"
                      values={{
                        unit: isMetric ? "MPa" : "ksi",
                      }}
                    />
                  </Text>
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("A_b")}
                      style={{
                        height: 7,
                        width: 9,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    <Trans
                      i18nKey="aashto.inner.Ab_Description"
                      values={{
                        unit: isMetric ? "mm²" : "in²",
                      }}
                    />
                  </Text>
                </View>
              </View>
            </>
          ) : null}

          <Text style={styles.sectionSubText}>{t("aashto.inner.Pd Calculation_pdf")}</Text>

          <View style={tableStyles.tableContainer}>
            {/* Bemessungslast */}
            <DataRow
              item={{
                title: t("designLoad", { ns: "fhwa" }),
                bezImage: {
                  uri: ABC_URI + "P_{d}",
                  height: 7,
                  width: 9,
                },
                value: designLoad,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <KiloNewton /> : <Kilopound />,
              }}
            />
            {/* Factor of Safety */}
            <DataRow
              item={{
                title: t("factorOfSafety", { ns: "fhwa" }),
                value: data.Fhwa.fhwaAll.factorOfSafety,
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
            {/* Ultimate load */}
            <DataRow
              item={{
                title: t("UltimateLoad", { ns: "fhwa" }),
                value: data.Fhwa.fhwaAll.ultimateLoad.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <KiloNewton /> : <Kilopound />,
              }}
            />
          </View>
          {/*
              
              Fd Calculation:
              Design Combination  Strength 1, Max
              Dead Load               20      [kip]x 1.25
              Live Load               30      [kip]x 1.75
              Total Design Load   Fd  77.5    [kip]
              */}
          {/* Fd Verification of the load-bearing capacity */}
          <Text style={styles.sectionSubText}>
            {t("aashto.inner.Fd Verification of the load-bearing capacity")}
          </Text>
          <View style={tableStyles.tableContainer}>
            {/* Selected Load Carrying Element: */}
            <DataRowTextValue
              item={{
                title: t("aashto.inner.Selected Load Carrying Element"),
                value: data.Fhwa.fhwaAll.selectedCarryingElement.name,
                itemIndex: itemIndex++,
              }}
            />
            {/* Bar OD */}
            <DataRow
              item={{
                title: t("aashto.inner.Bar OD"),
                value: data.Fhwa.fhwaAll.barOD.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <Millimetre /> : <Inch />,
              }}
            />
            {/* Area of Steel */}
            <DataRow
              item={{
                title: t("aashto.inner.Area of Steel"),
                bezImage: {
                  uri: ABC_URI + "A_{b}",
                  height: 7,
                  width: 9,
                },
                value: data.Fhwa.fhwaAll.areaOfSteel.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? "mm²" : "in²",
              }}
            />
            {/* Fy */}
            <DataRow
              item={{
                title: t("aashto.inner.Yield strength"),
                bezImage: {
                  uri: ABC_URI + "F_{y}",
                  height: 7,
                  width: 9,
                },
                value: data.Fhwa.fhwaAll.fy.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <Megapascal /> : <KilopoundPerSquareInch />,
              }}
            />
            {/* Py */}
            <DataRow
              item={{
                title: t("aashto.inner.Yield Load"),
                bezImage: {
                  uri: ABC_URI + "P_{y}",
                  height: 7,
                  width: 9,
                },
                value: data.Fhwa.fhwaAll.py.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <KiloNewton /> : <Kilopound />,
              }}
            />
            {/* Fy (Controlling) */}
            <DataRow
              item={{
                title: t("aashto.inner.Fy(Controlling)"),
                bezImage: {
                  uri: ABC_URI + "F_{y,c}",
                  height: 7,
                  width: 14,
                },
                value: data.Fhwa.fhwaAll.fyControlling.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <Megapascal /> : <KilopoundPerSquareInch />,
              }}
            />
            {/* Bit/Hole Size */}
            <DataRow
              item={{
                title: t("aashto.inner.Bit/Hole Size"),
                value: data.Fhwa.fhwaAll.holeSize.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <Millimetre /> : <Inch />,
              }}
            />
            {/*  28 day grout strength */}
            {isCompression && (
              <DataRow
                item={{
                  title: t("aashto.inner.Choose 28-day grout strength"),
                  bezImage: {
                    uri: ABC_URI + "f'_{c}",
                    height: 7,
                    width: 9,
                  },
                  value: data.Fhwa.fhwaAll.groutStrength.current,
                  valueScale: 2,
                  itemIndex: itemIndex++,
                  valueUnit: isMetric ? <Megapascal /> : <KilopoundPerSquareInch />,
                }}
              />
            )}

            {/* Area of Grout */}
            <DataRow
              item={{
                title: t("aashto.inner.Area of Grout"),
                bezImage: {
                  uri: ABC_URI + "A_{g}",
                  height: 7,
                  width: 9,
                },
                value: data.Fhwa.fhwaAll?.areaOfGrout?.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? "mm²" : "in²",
              }}
            />
          </View>
        </View>
      </PageWrapper>
      <PageWrapper Deckblatt={data.Deckblatt}>
        <View>
          <View style={tableStyles.tableContainer}>
            {isCompression && (
              <>
                <DataRow
                  item={{
                    title: t("SafetyFactorGroutDesign", { ns: "fhwa" }),
                    formulaText: t("FHWA Micropile", { ns: "fhwa" }),
                    bezImage: {
                      uri: ABC_URI + "\\alpha",
                      height: 4,
                      width: 5,
                    },
                    value: data.Fhwa.fhwaAll.safetyFactor.GroutFactor,
                    valueScale: 2,
                    itemIndex: itemIndex++,
                  }}
                />
                {/* Safety Factor Grout Test */}
                <DataRow
                  item={{
                    title: t("SafetyGroutTest", { ns: "fhwa" }),
                    bezImage: {
                      uri: ABC_URI + "\\beta",
                      height: 8,
                      width: 5,
                    },
                    value: data.Fhwa.fhwaAll.safetyFactor.GroutFactorTest,
                    valueScale: 2,
                    itemIndex: itemIndex++,
                  }}
                />
              </>
            )}
            {/* Safety Factor Steel (Yield) */}
            <DataRow
              item={{
                title: t("SafetyFactorSteel", { ns: "fhwa" }),
                bezImage: {
                  uri: ABC_URI + "\\gamma",
                  height: 6,
                  width: 4,
                },
                formulaText: t("FHWA Micropile", { ns: "fhwa" }),
                value: isCompression
                  ? data.Fhwa.fhwaAll.safetyFactor.Compression
                  : data.Fhwa.fhwaAll.safetyFactor.Tension,
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
            {/* Safety Factor Steel Test (Yield) */}
            <DataRow
              item={{
                title: t("SafetyFactorSteelTest", { ns: "fhwa" }),
                bezImage: {
                  uri: ABC_URI + "\\delta",
                  height: 6,
                  width: 4,
                },
                value: isCompression
                  ? data.Fhwa.fhwaAll.safetyFactor.CompressionTest
                  : data.Fhwa.fhwaAll.safetyFactor.TensionTest,
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
          </View>

          <View style={tableStyles.tableContainer}>
            {/* Allowable Load */}
            <DataRow
              item={{
                title: t("AllowableLoad", { ns: "fhwa" }),
                bezImage: {
                  uri: isTension ? ABC_URI + "P_{t-allowable}" : ABC_URI + "P_{c-allowable}",
                  height: 7,
                  width: 40,
                },
                formulaImage: {
                  uri: isTension
                    ? ABC_URI + "\\gamma\\cdot&space;F_{y-bar}\\cdot&space;A_{bar}"
                    : ABC_URI +
                      "\\alpha\\cdot&space;f'_{c}\\cdot&space;A_{grout}&plus;\\gamma\\cdot&space;F_{y-bar}\\cdot&space;A_{bar}",
                  height: 9,
                  width: isTension ? 74 : 145,
                },
                value: allowableLoad,
                valueUnit: isMetric ? <KiloNewton /> : <Kilopound />,
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
            {/* Maximum Test Load */}
            <DataRow
              item={{
                title: t("MaximumTestLoad", { ns: "fhwa" }),
                bezImage: {
                  uri: isTension ? ABC_URI + "P_{t-test}" : ABC_URI + "P_{c-test}",
                  height: 7,
                  width: 24,
                },
                formulaImage: {
                  uri: isTension
                    ? ABC_URI + "\\delta\\cdot&space;F_{y-bar}\\cdot&space;A_{bar}"
                    : ABC_URI +
                      "\\beta\\cdot&space;f'_{c}\\cdot&space;A_{grout}&plus;\\delta\\cdot&space;F_{y-bar}\\cdot&space;A_{bar}",
                  height: 9,
                  width: isTension ? 74 : 149,
                },
                value: allowableTestLoad,
                valueUnit: isMetric ? <KiloNewton /> : <Kilopound />,
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
          </View>

          <View style={tableStyles.tableContainer}>
            {/* Design Check: Pt-allowable || Pc-Allowable */}
            <DataRowTextValue
              item={{
                title: t("DesignCheck_pdf", { ns: "fhwa" }),
                bezImage: {
                  uri: isTension ? ABC_URI + "P_{t-allowable}" : ABC_URI + "P_{c-allowable}",
                  height: 7,
                  width: 40,
                },
                value: aL_isGreater ? ok : ng,
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
            {/* Design Check: Pc-Test || Pt-Test */}
            <DataRowTextValue
              item={{
                title: t("DesignCheck_pdf", { ns: "fhwa" }),
                bezImage: {
                  uri: isTension ? ABC_URI + "P_{t-test}" : ABC_URI + "P_{c-test}",
                  height: 7,
                  width: 24,
                },
                value: aTL_isGreater ? ok : ng,
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
          </View>
        </View>
      </PageWrapper>
    </>
  );
}
